import React, { useState, useEffect } from "react";
import PageLoading from '../PageLoading';
import { notificarError } from "../Almacenes/AlmacenNotify";

const DetallesAuditoriaMovimiento = ({ idMovimientoAlmacen }) => {
    const [auditoria, setAuditoria] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (idMovimientoAlmacen) {
            cargarAuditoria();
        }
    }, [idMovimientoAlmacen]);

    const cargarAuditoria = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(`/api/movimientosAlmacen/auditoria/${idMovimientoAlmacen}`);
            
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            setAuditoria(data || []);
        } catch (err) {
            console.error('Error al cargar auditoría:', err);
            setError(err.message);
            notificarError('Error al cargar los datos de auditoría');
        } finally {
            setLoading(false);
        }
    };

    const formatearCantidad = (cantidad) => {
        return parseFloat(cantidad || 0).toLocaleString('es-PE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const obtenerClaseMovimiento = (anterior, actual) => {
        if (actual > anterior) {
            return 'text-success'; // Verde para incrementos
        } else if (actual < anterior) {
            return 'text-danger'; // Rojo para decrementos
        }
        return 'text-muted'; // Gris para sin cambios
    };

    const obtenerIconoMovimiento = (anterior, actual) => {
        if (actual > anterior) {
            return <i className="fas fa-arrow-up text-success mr-1"></i>;
        } else if (actual < anterior) {
            return <i className="fas fa-arrow-down text-danger mr-1"></i>;
        }
        return <i className="fas fa-minus text-muted mr-1"></i>;
    };

    if (loading) {
        return <PageLoading />;
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <strong>Error:</strong> {error}
                <button 
                    className="btn btn-outline-danger btn-sm ml-3"
                    onClick={cargarAuditoria}
                >
                    <i className="fas fa-redo mr-1"></i>
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-body p-0">
                            {auditoria.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                                    <h6 className="text-muted">No se encontraron registros de auditoría</h6>
                                    <p className="text-muted small">
                                        Este movimiento no tiene cambios registrados en el stock
                                    </p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-striped mb-0">
                                        <thead className="">
                                            <tr>
                                                <th scope="col" className="border-0">
                                                    <i className="fas fa-box mr-2"></i>
                                                    Producto
                                                </th>
                                                <th scope="col" className="text-center border-0">
                                                    <i className="fas fa-history mr-2"></i>
                                                    Cantidad Anterior
                                                </th>
                                                <th scope="col" className="text-center border-0">
                                                    <i className="fas fa-exchange-alt mr-2"></i>
                                                    Cantidad Modificada
                                                </th>
                                                <th scope="col" className="text-center border-0">
                                                    <i className="fas fa-warehouse mr-2"></i>
                                                    Cantidad Actual
                                                </th>
                                                <th scope="col" className="text-center border-0">
                                                    <i className="fas fa-clock mr-2"></i>
                                                    Cantidad Grabada
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {auditoria.map((item, index) => {
                                                const diferencia = item.CantidadActual - item.CantidadAnterior;
                                                const porcentajeCambio = item.CantidadAnterior !== 0 
                                                    ? ((diferencia / item.CantidadAnterior) * 100).toFixed(1)
                                                    : '100.0';
                                                
                                                return (
                                                    <tr key={index} className="align-middle">
                                                        <td className="font-weight-medium">
                                                            <div className="d-flex align-items-center">
                                                                <div className="font-weight-bold text-dark">
                                                                    {item.Producto}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="badge badge-secondary badge-pill px-3 py-2">
                                                                {formatearCantidad(item.CantidadAnterior)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`badge badge-pill px-3 py-2 ${
                                                                item.CantidadModificada > 0 
                                                                    ? 'badge-success' 
                                                                    : item.CantidadModificada < 0 
                                                                        ? 'badge-danger' 
                                                                        : 'badge-warning'
                                                            }`}>
                                                                {item.CantidadModificada > 0 ? '+' : ''}
                                                                {formatearCantidad(item.CantidadModificada)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="badge badge-primary badge-pill px-3 py-2">
                                                                {formatearCantidad(item.CantidadActual)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="badge badge-success badge-pill px-3 py-2">
                                                                {item.CantidadGrabada ? formatearCantidad(item.CantidadGrabada) : '-'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetallesAuditoriaMovimiento;